import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Stack, Typography } from "@mui/material";
import {
  clearSkillAgentScrapingBeeCreditsBlock,
  getSkillAgentScrapingBeeCredits,
} from "../../api/Skills/skillAgentSearchesApi";

/**
 * Aviso tipo OpenAI: sin créditos o saldo bajo de ScrapingBee.
 * `initial` puede venir de /meta.scrapingBee.
 */
export default function SkillAgentScrapingBeeCreditsAlert({
  initial = null,
  sx,
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(initial);
  const [clearing, setClearing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await getSkillAgentScrapingBeeCredits();
      setStatus(data);
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    if (initial) setStatus(initial);
  }, [initial]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!status?.enabled) return null;
  const blocked = Boolean(status.scrapingBeeCreditsBlocked);
  const warn = Boolean(status.scrapingBeeCreditsWarn);
  if (!blocked && !warn) return null;

  const remaining = status.scrapingBeeCredits?.remaining;
  const max = status.scrapingBeeCredits?.max;
  const detail =
    remaining != null && max != null
      ? t("skillAgent.scrapingBee.creditsLeft", {
          remaining,
          max,
          defaultValue: "Quedan {{remaining}} de {{max}} créditos.",
        })
      : null;

  const onClear = async () => {
    setClearing(true);
    try {
      const data = await clearSkillAgentScrapingBeeCreditsBlock();
      setStatus(data);
    } catch {
      await refresh();
    } finally {
      setClearing(false);
    }
  };

  return (
    <Alert
      severity={blocked ? "error" : "warning"}
      sx={{ mb: 1.5, ...sx }}
      action={
        blocked ? (
          <Button
            color="inherit"
            size="small"
            disabled={clearing}
            onClick={onClear}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {t("skillAgent.scrapingBee.clearBlock", {
              defaultValue: "Ya recargué ScrapingBee — desbloquear",
            })}
          </Button>
        ) : null
      }
    >
      <Stack spacing={0.25}>
        <Typography variant="subtitle2" fontWeight={800}>
          {blocked
            ? t("skillAgent.scrapingBee.blockedTitle", {
                defaultValue: "Sin créditos ScrapingBee",
              })
            : t("skillAgent.scrapingBee.warnTitle", {
                defaultValue: "ScrapingBee bajo de créditos",
              })}
        </Typography>
        <Typography variant="body2">
          {status.scrapingBeeCreditsMessage ||
            (blocked
              ? t("skillAgent.scrapingBee.blocked", {
                  defaultValue:
                    "Sin créditos en ScrapingBee. Las webs muradas (Mr Porter, Hermès…) no se scrapean hasta recargar.",
                })
              : t("skillAgent.scrapingBee.warn", {
                  defaultValue:
                    "ScrapingBee va justo de créditos. Recarga el plan antes del próximo SEARCH con webs muradas.",
                }))}
        </Typography>
        {detail ? (
          <Typography variant="caption" color="text.secondary">
            {detail}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  );
}
